import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Car, Shield, Clock, Users, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/Logo";

const Index = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthAndRedirect();
  }, []);

  const checkAuthAndRedirect = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session) {
      // User is authenticated, check their role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);
      
      const hasProviderRole = roleData?.some(r => r.role === "provider");
      if (hasProviderRole) {
        navigate("/provider-dashboard");
      } else {
        navigate("/dashboard");
      }
      return;
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section — compressed to viewport on mobile */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-accent text-white overflow-hidden min-h-[100svh] md:min-h-0 flex flex-col justify-center">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        
        {/* Logo in corner */}
        <div className="absolute top-4 left-4 z-20 pt-safe">
          <Logo size="lg" className="text-white" />
        </div>
        
        <div className="container mx-auto px-4 py-16 pt-24 md:py-20 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 md:mb-6 animate-fade-in">
              Drive Your Growth
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-6 md:mb-8 text-white/90">
              Business to Business Vehicle Hiring - done right
            </p>
            <div className="flex flex-col gap-4 md:gap-6 justify-center items-center">
              <Button
                size="lg"
                onClick={() => navigate("/order")}
                className="text-lg sm:text-xl px-8 sm:px-12 py-6 sm:py-7 shadow-2xl hover:shadow-3xl transition-all hover:scale-105 bg-white text-primary hover:bg-white/90 font-bold w-full sm:w-auto touch-target"
              >
                <Car className="w-6 h-6 mr-2" />
                Book a Vehicle
              </Button>
              <div className="flex flex-row gap-3 w-full sm:w-auto">
                <Button
                  size="default"
                  variant="secondary"
                  onClick={() => navigate("/customer-signin")}
                  className="px-6 shadow-lg hover:shadow-xl transition-all flex-1 sm:flex-none touch-target"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
                <Button
                  size="default"
                  variant="outline"
                  onClick={() => navigate("/customer-signup")}
                  className="px-6 shadow-lg hover:shadow-xl transition-all bg-white/10 hover:bg-white/20 text-white border-white/30 flex-1 sm:flex-none touch-target"
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Go24 Sub - for business
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Verified Suppliers</h3>
                <p className="text-muted-foreground">
                  We rigorously vet every supplier to ensure reliability, quality service, and predictable pricing you can count on.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Fast - Automatic Process</h3>
                <p className="text-muted-foreground">
                  Every vehicle is inspected twice before handover. Contracting happens instantly through secure e-contracts.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Real-Time Availability</h3>
                <p className="text-muted-foreground">
                  See what's available instantly. Serve your clients faster, close deals quicker, and drive real growth.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How It Works
          </h2>
          <div className="max-w-2xl mx-auto">
            <ol className="space-y-6">
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-lg">1</span>
                <div>
                  <p className="font-medium text-lg">Search for your vehicle</p>
                  <p className="text-muted-foreground">Browse available vehicles by dates, location, and specifications</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-lg">2</span>
                <div>
                  <p className="font-medium text-lg">Choose the vehicle from the selection</p>
                  <p className="text-muted-foreground">Pick the perfect vehicle from our verified supplier network</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-lg">3</span>
                <div>
                  <p className="font-medium text-lg">Pay for professional delivery or pick up</p>
                  <p className="text-muted-foreground">Get confirmation and select whether you'll be picking the vehicle up or we will deliver it where you need it</p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            No more endless calls, price haggling, or guessing what's available. Just real-time vehicles, fixed pricing, and instant booking.
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => navigate("/order")}
            className="text-lg px-8 shadow-lg hover:shadow-xl transition-all"
          >
            <Car className="w-5 h-5 mr-2" />
            Book a Vehicle
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-background border-t pb-safe">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">© {new Date().getFullYear()} Go24. Real-time vehicles. Fixed pricing. Instant booking.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-3">
            <button 
              onClick={() => navigate("/privacy-policy")}
              className="text-sm text-muted-foreground hover:text-primary transition-colors touch-target flex items-center justify-center"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => navigate("/terms-of-service")}
              className="text-sm text-muted-foreground hover:text-primary transition-colors touch-target flex items-center justify-center"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => navigate("/reliability-score")}
              className="text-sm text-muted-foreground hover:text-primary transition-colors touch-target flex items-center justify-center"
            >
              Reliability Score
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-2">
            <button 
              onClick={() => navigate("/join")}
              className="text-sm text-muted-foreground/50 hover:text-muted-foreground transition-colors touch-target flex items-center justify-center"
            >
              Supplier Portal
            </button>
            <button 
              onClick={() => navigate("/admin-auth")}
              className="text-xs text-muted-foreground/30 hover:text-muted-foreground/50 transition-colors touch-target flex items-center justify-center"
            >
              Admin
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;